import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Sparkles,
  Calendar,
  ArrowRight,
  Flame,
  Mail,
  Phone,
  User,
  MapPin,
  Heart,
  Activity,
  Compass,
  Shield,
  GraduationCap,
  HeartHandshake,
  Home,
  ShieldAlert,
  Trophy,
  CheckCircle,
  Clock,
  ChevronLeft,
  ChevronRight,
  Volume2,
  VolumeX
} from 'lucide-react';
import { FaPlay } from 'react-icons/fa';
import WhatsAppIcon from '../components/WhatsAppIcon';
import '../styles/HomamPortal.css';
import '../styles/HomamRegistration.css';
import '../styles/Home.css';
import '../styles/ShortsGallery.css';
import {
  MOCK_NAKSHATRAS,
  HOMAM_BENEFITS,
  getWhatsAppLink,
  getNakshatraImage
} from '../data/homamData';
import { HOMAM_27_VIDEOS } from '../data/homamVideosData';
import { getHomamPath } from '../utils/domain';

/**
 * ⚡ High-Performance Lazy Video Reel Card Component
 * - Only mounts/streams video when visible in viewport (IntersectionObserver)
 * - Renders an instant lightweight (~100 KB) frame poster prior to stream
 * - Automatically pauses when scrolled away or when the Modal Player is active
 * - Uses hardware-accelerated, lightweight Cloudinary stream (~4MB vs 96MB)
 */
function HomamCardVideoItem({ item, idx, isUnmuted, isModalOpen, onToggleAudio, onOpenModal }) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      { root: null, rootMargin: '120px', threshold: 0.15 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Pause card video whenever modal is open or card scrolls out of view
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (!isInView || isModalOpen) {
      video.pause();
    } else {
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {});
      }
    }
  }, [isInView, isModalOpen]);

  return (
    <div
      className="homam-video-card-slanted"
      ref={containerRef}
      onClick={onOpenModal}
      title={`${item.title} - Click to expand`}
    >
      {/* TOP LEFT DAY BADGE */}
      <div className="homam-card-day-badge">{item.dayBadge}</div>

      {/* VIDEO WRAPPER WITH LAZY STREAMING */}
      <div className="homam-card-video-wrap">
        {isInView ? (
          <video
            ref={videoRef}
            src={item.cardVideoSrc || item.videoSrc || item.localUrl}
            poster={item.posterSrc}
            className="homam-card-video-element"
            autoPlay={!isModalOpen}
            muted={!isUnmuted}
            loop
            playsInline
            preload="metadata"
            onCanPlay={(e) => {
              if (!isModalOpen) {
                e.target.play().catch(() => {});
              }
            }}
            onError={(e) => {
              if (item.localUrl && !e.target.src.includes(item.localUrl)) {
                e.target.src = item.localUrl;
              } else if (!e.target.src.includes('/videos/promo_video_01.mp4')) {
                e.target.src = '/videos/promo_video_01.mp4';
              }
            }}
          />
        ) : (
          item.posterSrc && (
            <img
              src={item.posterSrc}
              alt={item.title}
              className="homam-card-video-element"
              loading="lazy"
              onError={(e) => {
                e.target.src = '/images/sai_sahasranama_ayushya_homam_banner.jpg';
              }}
            />
          )
        )}

        {/* AUDIO TOGGLE BUTTON */}
        <button
          type="button"
          className="homam-card-audio-toggle"
          onClick={(e) => {
            e.stopPropagation();
            onToggleAudio();
          }}
          title={isUnmuted ? "Mute audio" : "Unmute audio to listen"}
        >
          {isUnmuted ? (
            <Volume2 className="w-4 h-4 text-yellow-300" />
          ) : (
            <VolumeX className="w-4 h-4 text-slate-300" />
          )}
        </button>
      </div>

      {/* SLIM COMPACT BOTTOM PILL - UNOBTRUSIVE */}
      <div className="homam-card-compact-label">
        <span className="homam-card-compact-title">
          {item.nakshatraEn} • {item.assignedDate}
        </span>
        <span className="homam-card-compact-watch">
          <FaPlay className="w-2 h-2" /> Expand
        </span>
      </div>
    </div>
  );
}

export default function HomamRegistrationPage() {
  const navigate = useNavigate();
  const [selectedStarId, setSelectedStarId] = useState(1);
  const [bookingForm, setBookingForm] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    whatsappNumber: '',
    location: ''
  });

  const [bookingError, setBookingError] = useState(null);
  const [successModalData, setSuccessModalData] = useState(null);

  // 🎥 27-Day Sacred Homam Videos State
  const [activeVideoDayIndex, setActiveVideoDayIndex] = useState(null);
  const [unmutedCardIndex, setUnmutedCardIndex] = useState(null);
  const videoTrackRef = useRef(null);

  const scrollVideos = (dir) => {
    if (videoTrackRef.current) {
      const amount = dir === 'left' ? -380 : 380;
      videoTrackRef.current.scrollBy({ left: amount, behavior: 'smooth' });
    }
  };

  const scrollToBookingForm = (starId = null) => {
    if (starId) {
      setSelectedStarId(starId);
    }
    const formEl = document.getElementById('booking-form') || document.getElementById('find-star');
    if (formEl) {
      formEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // 🔒 Lock background scroll when video modal is active
  useEffect(() => {
    if (activeVideoDayIndex !== null) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [activeVideoDayIndex]);

  // Keyboard navigation for video modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (activeVideoDayIndex === null) return;
      if (e.key === 'Escape') {
        setActiveVideoDayIndex(null);
      } else if (e.key === 'ArrowRight') {
        setActiveVideoDayIndex((prev) => (prev + 1) % HOMAM_27_VIDEOS.length);
      } else if (e.key === 'ArrowLeft') {
        setActiveVideoDayIndex((prev) => (prev - 1 + HOMAM_27_VIDEOS.length) % HOMAM_27_VIDEOS.length);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeVideoDayIndex]);

  const selectedStar =
    MOCK_NAKSHATRAS.find((n) => n.id === Number(selectedStarId)) || MOCK_NAKSHATRAS[0];

  const handleBookingFormChange = (e) => {
    const { name, value } = e.target;
    setBookingForm((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === 'phoneNumber') {
        updated.whatsappNumber = value;
      }
      return updated;
    });
    if (bookingError) setBookingError(null);
  };

  const handleBookingSubmit = (e) => {
    e.preventDefault();
    setBookingError(null);

    if (
      !bookingForm.name.trim() ||
      !bookingForm.email.trim() ||
      !bookingForm.phoneNumber.trim() ||
      !bookingForm.whatsappNumber.trim() ||
      !bookingForm.location.trim()
    ) {
      setBookingError('Please fill in all required fields (Name, Email, Phone Number, WhatsApp Number, Location).');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(bookingForm.email.trim())) {
      setBookingError('Please enter a valid email address.');
      return;
    }

    const cleanPhone = bookingForm.phoneNumber.replace(/\D/g, '');
    if (cleanPhone.length < 10) {
      setBookingError('Please enter a valid 10-digit Phone number.');
      return;
    }

    const cleanWa = bookingForm.whatsappNumber.replace(/\D/g, '');
    if (cleanWa.length < 10) {
      setBookingError('Please enter a valid 10-digit WhatsApp number.');
      return;
    }

    let activeUser = null;
    try {
      const stored = localStorage.getItem('user');
      if (stored) activeUser = JSON.parse(stored);
    } catch (_) {}

    const devoteePayload = {
      name: bookingForm.name.trim(),
      mobile: cleanPhone,
      whatsappNumber: cleanWa,
      email: bookingForm.email.trim(),
      userId: activeUser?._id || null,
      city: bookingForm.location.trim(),
      gothram: '',
      rasi: '',
      message: '',
      nakshatraId: Number(selectedStar.id),
      nakshatraNameEn: selectedStar.nameEn,
      nakshatraNameTa: selectedStar.nameTa,
      homamDate: selectedStar.assignedDate || '',
      preferredDay: `Day ${selectedStar.orderIndex} • ${selectedStar.assignedDate}`,
      participantsCount: 1,
      source: 'Homam Registration Page (/homam-registration)',
      bookingId: `SGSMH${Date.now().toString().slice(-6)}`,
      _id: 'pending_checkout',
    };

    // Move directly to Seva Plan Selection & Booking
    navigate(getHomamPath('/booking'), {
      state: {
        booking: devoteePayload,
        nakshatra: selectedStar,
      },
    });
  };

  const getBenefitIcon = (iconName) => {
    switch (iconName) {
      case 'Heart':
        return <Heart className="w-5 h-5" />;
      case 'Activity':
        return <Activity className="w-5 h-5" />;
      case 'Compass':
        return <Compass className="w-5 h-5" />;
      case 'Flame':
        return <Flame className="w-5 h-5" />;
      case 'Shield':
        return <Shield className="w-5 h-5" />;
      case 'GraduationCap':
        return <GraduationCap className="w-5 h-5" />;
      case 'HeartHandshake':
        return <HeartHandshake className="w-5 h-5" />;
      case 'Home':
        return <Home className="w-5 h-5" />;
      case 'ShieldAlert':
        return <ShieldAlert className="w-5 h-5" />;
      case 'Trophy':
        return <Trophy className="w-5 h-5" />;
      default:
        return <Sparkles className="w-5 h-5" />;
    }
  };

  const templeWhatsApp = getWhatsAppLink({
    customMessage: `Namaste Sri Gnana Sai Baba Mandir 🙏\nI have registered for the 27 Nakshatra Maha Homam (Token: ${successModalData?.bookingId || ''}). Please confirm my seva details.`,
    source: 'Registration Modal'
  });

  return (
    <div className="homam-reg-page">

      {/* ================= 27-DAY SACRED HOMAM VIDEOS SECTION WITH 2-DEGREE SLANT & BLINKING CTA ================= */}
      <section className="homam-27videos-section">
        <div className="homam-videos-header">
          <div className="homam-videos-header-left">
            <div className="homam-videos-badge">
              <Sparkles className="badge-icon" />
              <span>DAILY SACRED HOMAM VIDEOS</span>
            </div>
            <h2 className="homam-videos-title">
              Nakshatra Homam <span className="title-accent">Sacred Videos & Darshan</span>
            </h2>
            <p className="homam-videos-sub">
              Watch sacred birth star rituals, planetary peace ahutis, and divine darshan videos blessed by Guruji.
            </p>
          </div>

          <div className="homam-videos-header-right">
            <button
              type="button"
              className="homam-scroll-btn"
              onClick={() => scrollVideos('left')}
              aria-label="Scroll left"
              title="Scroll Left"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              className="homam-scroll-btn"
              onClick={() => scrollVideos('right')}
              aria-label="Scroll right"
              title="Scroll Right"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* HORIZONTAL SCROLLING TRACK WITH 2-DEGREE SLANTED CARDS (LAZY LOADED & ACCELERATED) */}
        <div className="homam-27videos-track" ref={videoTrackRef}>
          {HOMAM_27_VIDEOS.map((item, idx) => (
            <HomamCardVideoItem
              key={item.id || idx}
              item={item}
              idx={idx}
              isUnmuted={unmutedCardIndex === idx}
              isModalOpen={activeVideoDayIndex !== null}
              onToggleAudio={() => setUnmutedCardIndex(unmutedCardIndex === idx ? null : idx)}
              onOpenModal={() => setActiveVideoDayIndex(idx)}
            />
          ))}
        </div>

        {/* ================= BLINKING "BOOK NOW" CALL TO ACTION ================= */}
        <div className="homam-videos-cta-wrap">
          <button
            type="button"
            className="homam-blink-book-btn"
            onClick={() => scrollToBookingForm()}
            aria-label="Book Sacred Homam Now"
          >
            <Flame className="btn-flame-icon" />
            <span>Book Sacred Homam Now • உங்கள் நட்சத்திர ஹோமத்தை இப்போதே முன்பதிவு செய்யுங்கள்</span>
            <ArrowRight className="btn-arrow-icon" />
          </button>
          <p className="homam-blink-cta-sub">
            ✦ Registration open for all 27 auspicious days • Click above to register for your birth star
          </p>
        </div>
      </section>

      {/* 27-DAY VIDEO MODAL PLAYER (STREAMING OPTIMIZED 720P) */}
      {activeVideoDayIndex !== null && HOMAM_27_VIDEOS[activeVideoDayIndex] && (
        <div
          className="homam-video-modal-overlay"
          onClick={() => setActiveVideoDayIndex(null)}
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          <div
            className="homam-video-modal-card"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="homam-video-modal-close"
              onClick={() => setActiveVideoDayIndex(null)}
              title="Close Video"
            >
              ✕
            </button>

            <div className="homam-modal-video-wrap">
              {HOMAM_27_VIDEOS[activeVideoDayIndex].videoSrc &&
              (HOMAM_27_VIDEOS[activeVideoDayIndex].videoSrc.endsWith('.mp4') ||
                HOMAM_27_VIDEOS[activeVideoDayIndex].videoSrc.includes('cloudinary.com')) ? (
                <video
                  key={HOMAM_27_VIDEOS[activeVideoDayIndex].id}
                  src={
                    HOMAM_27_VIDEOS[activeVideoDayIndex].modalVideoSrc ||
                    HOMAM_27_VIDEOS[activeVideoDayIndex].videoSrc
                  }
                  poster={HOMAM_27_VIDEOS[activeVideoDayIndex].posterSrc}
                  controls
                  autoPlay
                  playsInline
                  preload="auto"
                  className="homam-modal-video-player"
                  onError={(e) => {
                    // Fallback to demo promo video if file not yet uploaded
                    if (!e.target.src.includes('promo_video_01.mp4')) {
                      e.target.src = '/videos/promo_video_01.mp4';
                    }
                  }}
                />
              ) : (
                <iframe
                  src={HOMAM_27_VIDEOS[activeVideoDayIndex].videoSrc}
                  title={HOMAM_27_VIDEOS[activeVideoDayIndex].title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="homam-modal-video-player"
                />
              )}
            </div>

            <div className="homam-modal-details-bar">
              <div className="homam-modal-info-left">
                <span className="homam-modal-day-pill">
                  <Sparkles className="w-3.5 h-3.5" />
                  {HOMAM_27_VIDEOS[activeVideoDayIndex].dayBadge} • {HOMAM_27_VIDEOS[activeVideoDayIndex].assignedDate}
                </span>
                <h3 className="homam-modal-star-title">
                  {HOMAM_27_VIDEOS[activeVideoDayIndex].nakshatraEn} ({HOMAM_27_VIDEOS[activeVideoDayIndex].nakshatraTa})
                </h3>
                <p className="homam-modal-info-sub">
                  Deity: {HOMAM_27_VIDEOS[activeVideoDayIndex].deity}
                </p>
              </div>

              <div className="homam-modal-actions">
                <div className="homam-modal-nav-group">
                  <button
                    type="button"
                    className="homam-modal-nav-btn"
                    onClick={() =>
                      setActiveVideoDayIndex(
                        (activeVideoDayIndex - 1 + HOMAM_27_VIDEOS.length) % HOMAM_27_VIDEOS.length
                      )
                    }
                    title="Previous Day"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" /> Prev Day
                  </button>
                  <button
                    type="button"
                    className="homam-modal-nav-btn"
                    onClick={() =>
                      setActiveVideoDayIndex((activeVideoDayIndex + 1) % HOMAM_27_VIDEOS.length)
                    }
                    title="Next Day"
                  >
                    Next Day <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
                <button
                  type="button"
                  className="homam-modal-book-star-btn"
                  onClick={() => {
                    const starDay = HOMAM_27_VIDEOS[activeVideoDayIndex].dayNumber;
                    setActiveVideoDayIndex(null);
                    scrollToBookingForm(starDay);
                  }}
                  title="Book for Homam"
                >
                  Book for Homam
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= 2. EXACT HOMAM BOOKING CONSOLE (AS IN FIRST IMAGE) ================= */}
      <section
        id="find-star"
        className="homam-grid-wrap"
        style={{
          background: 'linear-gradient(180deg, #FAF7F2 0%, #F4EFE6 50%, #FAF7F2 100%)',
          borderTop: '1px solid #EADDC7',
          padding: '60px 20px'
        }}
      >
        <div className="homam-container">
          <div className="homam-grid-head" style={{ textAlign: 'center', marginBottom: 36 }}>
            <div className="homam-emblem-badge" style={{ marginBottom: 10, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Sparkles className="w-3.5 h-3.5" style={{ color: '#b8860b' }} />
              <span>Sacred Homam Registration</span>
            </div>
            <h2 style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)', fontWeight: 900, color: '#0f2d5c', margin: '0 0 6px 0' }}>
              Book for 27 Nakshatra Maha Homam
            </h2>
            <p className="tamil-sub" style={{ fontSize: 'clamp(1.05rem, 1.8vw, 1.35rem)', color: '#946608', fontWeight: 800, margin: '0 0 10px 0' }}>
              உங்கள் ஜென்ம நட்சத்திர ஹோமத்திற்கு இப்போதே முன்பதிவு செய்யுங்கள்
            </p>
            <p style={{ fontSize: 14.5, color: '#475569', maxWidth: 780, margin: '0 auto' }}>
              Enter your devotee details below to register for the sacred homam. Select your birth star to participate on its dedicated day.
            </p>
          </div>

          <div className="homam-console-wrapper">
            {/* Master Sacred Sanctuary Console */}
            <div id="booking-form" className="homam-master-console">
              {/* Top Sacred Ribbon */}
              <div className="homam-console-ribbon">
                <div className="homam-console-ribbon-text">
                  <span className="homam-console-ribbon-title">
                    27 NAKSHATRA MAHA HOMAM • HOMAM BOOKING CONSOLE
                  </span>
                  <span className="homam-console-ribbon-sub">
                    நேரடி ஹோம முன்பதிவு மற்றும் நட்சத்திர விவரங்கள்
                  </span>
                </div>

                <div className="homam-console-ribbon-badge">
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981', display: 'inline-block', boxShadow: '0 0 8px #10b981', flexShrink: 0 }}></span>
                  <span>Registration Open for All 27 Stars</span>
                </div>
              </div>

              {/* 2-Column Integrated Console */}
              <div className="homam-console-grid">
                {/* Left Card: Devotee Registration Form */}
                <div className="homam-console-card homam-console-card-left">
                  <div className="homam-console-card-inner">
                    <div>
                      <div className="homam-console-card-header">
                        <div style={{ width: 38, height: 38, borderRadius: 10, background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#b8860b', flexShrink: 0 }}>
                          <Flame className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 900, color: '#0f2d5c' }}>
                            Devotee Homam Details
                          </h3>
                          <span style={{ fontSize: '0.82rem', color: '#946608', fontWeight: 700 }}>
                            முன்பதிவு விவரங்களை உள்ளிடுக
                          </span>
                        </div>
                      </div>

                      {bookingError && (
                        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '10px 14px', borderRadius: 10, fontSize: 13, marginBottom: 14 }}>
                          {bookingError}
                        </div>
                      )}

                      <form id="homamDevoteeForm" onSubmit={handleBookingSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        {/* Row 1: Name & Email */}
                        <div className="homam-console-form-row">
                          <div className="homam-field">
                            <label style={{ fontSize: 12.5, fontWeight: 700, color: '#0f2d5c', display: 'flex', alignItems: 'center', gap: 5 }}>
                              <User className="w-3.5 h-3.5" style={{ color: '#b8860b' }} />
                              <span>Devotee Name *</span>
                            </label>
                            <input
                              type="text"
                              name="name"
                              placeholder="Full name"
                              value={bookingForm.name}
                              onChange={handleBookingFormChange}
                              required
                              style={{ width: '100%', padding: '11px 13px', borderRadius: 10, border: '1px solid #EADDC7', background: '#FAF7F2', fontSize: 13, color: '#0f2d5c', outline: 'none', boxSizing: 'border-box' }}
                            />
                          </div>

                          <div className="homam-field">
                            <label style={{ fontSize: 12.5, fontWeight: 700, color: '#0f2d5c', display: 'flex', alignItems: 'center', gap: 5 }}>
                              <Mail className="w-3.5 h-3.5" style={{ color: '#b8860b' }} />
                              <span>Email Address *</span>
                            </label>
                            <input
                              type="email"
                              name="email"
                              placeholder="devotee@gmail.com"
                              value={bookingForm.email}
                              onChange={handleBookingFormChange}
                              required
                              style={{ width: '100%', padding: '11px 13px', borderRadius: 10, border: '1px solid #EADDC7', background: '#FAF7F2', fontSize: 13, color: '#0f2d5c', outline: 'none', boxSizing: 'border-box' }}
                            />
                          </div>
                        </div>

                        {/* Row 2: Phone Number & WhatsApp Number */}
                        <div className="homam-console-form-row">
                          <div className="homam-field">
                            <label style={{ fontSize: 12.5, fontWeight: 700, color: '#0f2d5c', display: 'flex', alignItems: 'center', gap: 5 }}>
                              <Phone className="w-3.5 h-3.5" style={{ color: '#b8860b' }} />
                              <span>Phone Number *</span>
                            </label>
                            <input
                              type="tel"
                              name="phoneNumber"
                              maxLength={10}
                              placeholder="10-digit phone number"
                              value={bookingForm.phoneNumber}
                              onChange={handleBookingFormChange}
                              required
                              style={{ width: '100%', padding: '11px 13px', borderRadius: 10, border: '1px solid #EADDC7', background: '#FAF7F2', fontSize: 13, color: '#0f2d5c', outline: 'none', boxSizing: 'border-box' }}
                            />
                          </div>

                          <div className="homam-field">
                            <label style={{ fontSize: 12.5, fontWeight: 700, color: '#0f2d5c', display: 'flex', alignItems: 'center', gap: 5 }}>
                              <WhatsAppIcon size={14} style={{ color: '#25D366' }} />
                              <span>WhatsApp Number *</span>
                            </label>
                            <input
                              type="tel"
                              name="whatsappNumber"
                              maxLength={10}
                              placeholder="10-digit number"
                              value={bookingForm.whatsappNumber}
                              onChange={handleBookingFormChange}
                              required
                              style={{ width: '100%', padding: '11px 13px', borderRadius: 10, border: '1px solid #EADDC7', background: '#FAF7F2', fontSize: 13, color: '#0f2d5c', outline: 'none', boxSizing: 'border-box' }}
                            />
                          </div>
                        </div>

                        {/* Row 3: City / Location */}
                        <div className="homam-field">
                          <label style={{ fontSize: 12.5, fontWeight: 700, color: '#0f2d5c', display: 'flex', alignItems: 'center', gap: 5 }}>
                            <MapPin className="w-3.5 h-3.5" style={{ color: '#b8860b' }} />
                            <span>City / Location *</span>
                          </label>
                          <input
                            type="text"
                            name="location"
                            placeholder="e.g. Chennai, Bengaluru"
                            value={bookingForm.location}
                            onChange={handleBookingFormChange}
                            required
                            style={{ width: '100%', padding: '11px 13px', borderRadius: 10, border: '1px solid #EADDC7', background: '#FAF7F2', fontSize: 13, color: '#0f2d5c', outline: 'none', boxSizing: 'border-box' }}
                          />
                        </div>

                        {/* Row 4: Nakshatra Dropdown with Date */}
                        <div className="homam-field">
                          <label style={{ fontSize: 12.5, fontWeight: 700, color: '#0f2d5c', display: 'flex', alignItems: 'center', gap: 5 }}>
                            <Calendar className="w-3.5 h-3.5" style={{ color: '#b8860b' }} />
                            <span>Select Birth Star (Nakshatra) & Dedicated Homam Date *</span>
                          </label>
                          <select
                            name="nakshatraId"
                            value={selectedStarId}
                            onChange={(e) => {
                              const newId = Number(e.target.value);
                              setSelectedStarId(newId);
                            }}
                            required
                            style={{
                              width: '100%',
                              maxWidth: '100%',
                              minWidth: 0,
                              padding: '12px 14px',
                              borderRadius: 10,
                              border: '1.5px solid #d4a017',
                              background: '#FAF7F2',
                              fontSize: 13.5,
                              fontWeight: 700,
                              color: '#0f2d5c',
                              outline: 'none',
                              cursor: 'pointer',
                              boxSizing: 'border-box',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {MOCK_NAKSHATRAS.map((star) => (
                              <option key={star.id} value={star.id}>
                                Day {star.orderIndex}: {star.nameEn} ({star.nameTa}) — {star.assignedDate}
                              </option>
                            ))}
                          </select>
                        </div>
                      </form>
                    </div>

                    {/* Submit Action (Flushed to bottom to align with View Details button) */}
                    <div style={{ marginTop: 24, paddingTop: 6 }}>
                      <button
                        type="submit"
                        form="homamDevoteeForm"
                        className="btn-homam-submit"
                        style={{
                          width: '100%',
                          height: 48,
                          minHeight: 48,
                          maxHeight: 48,
                          padding: '0 20px',
                          borderRadius: 12,
                          background: 'linear-gradient(135deg, #f5c542 0%, #eab308 40%, #d4a017 100%)',
                          color: '#0a192f',
                          fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                          fontWeight: 800,
                          fontSize: 14.5,
                          border: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 8,
                          boxSizing: 'border-box',
                          boxShadow: '0 4px 14px rgba(212, 160, 23, 0.35)'
                        }}
                      >
                        <span>Book for Homam</span>
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Right Card: Celestial Nakshatra Deity Altar (Light Theme) */}
                <div className="homam-console-card homam-console-card-right">
                  <div className="homam-console-card-inner">
                    <div>
                      {/* Header Strip */}
                      <div className="homam-altar-header-strip">
                        <div className="homam-trending-day-badge">
                          <div className="homam-trending-cal-icon-chip">
                            <Calendar className="w-3.5 h-3.5" />
                          </div>
                          <span className="homam-trending-day-text">
                            <span>DAY {selectedStar.orderIndex}</span>
                            <span className="homam-trending-day-dot"></span>
                            <span>27 MAHA HOMAM</span>
                          </span>
                        </div>

                        <div className="homam-altar-date-chips">
                          <div className="homam-altar-pill homam-altar-pill-date">
                            <Calendar className="w-3 h-3" />
                            <span>{selectedStar.assignedDate || 'Date to be announced'}</span>
                          </div>
                          <div className="homam-altar-pill homam-altar-pill-time">
                            <Clock className="w-3 h-3" />
                            <span>9:00 AM – 11:00 AM</span>
                          </div>
                        </div>
                      </div>

                      {/* Star Deity Spotlight */}
                      <div className="homam-altar-deity-row">
                        <img
                          src={getNakshatraImage(selectedStar)}
                          alt={selectedStar.nameEn}
                          className="homam-altar-deity-img"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = '/images/nakshatra_homam_floating.jpg';
                          }}
                        />
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <h3 style={{ fontSize: 'clamp(1.4rem, 2.5vw, 1.85rem)', fontWeight: 900, color: '#0f2d5c', margin: 0, lineHeight: 1.15, wordBreak: 'break-word' }}>
                            {selectedStar.nameEn}
                          </h3>
                          <span style={{ fontSize: 'clamp(1.1rem, 2vw, 1.35rem)', fontWeight: 800, color: '#946608', display: 'block', marginTop: 2, wordBreak: 'break-word' }}>
                            {selectedStar.nameTa}
                          </span>
                        </div>
                      </div>

                      {/* 4 Cosmic Attribute Light Chips */}
                      <div className="homam-altar-chips-grid">
                        <div className="homam-altar-chip">
                          <span>Presiding Deity</span>
                          <strong>{selectedStar.deity ? selectedStar.deity.split('(')[0] : 'Lord Varuna'}</strong>
                        </div>
                        <div className="homam-altar-chip">
                          <span>Ruling Planet</span>
                          <strong>{selectedStar.rulingPlanet || selectedStar.rulerPlanet} {selectedStar.rulingPlanetTa ? `(${selectedStar.rulingPlanetTa})` : ''}</strong>
                        </div>
                        <div className="homam-altar-chip">
                          <span>Sacred Symbol</span>
                          <strong>{selectedStar.symbol}</strong>
                        </div>
                        <div className="homam-altar-chip">
                          <span>Zodiac (Rasi)</span>
                          <strong>{selectedStar.rasi}</strong>
                        </div>
                      </div>

                      {/* Short Description */}
                      <p style={{ fontSize: 12.5, color: '#475569', lineHeight: 1.5, margin: '0 0 10px 0', fontStyle: 'italic', wordBreak: 'break-word' }}>
                        &ldquo;{selectedStar.shortDescription}&rdquo;
                      </p>

                      {/* Sacred Moola Mantra Scroll */}
                      <div className="homam-altar-mantra">
                        <span style={{ fontSize: 10, color: '#92400e', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 2 }}>
                          SACRED MOOLA MANTRA:
                        </span>
                        <p style={{ margin: 0, fontWeight: 700, color: '#0f2d5c', fontFamily: 'monospace', fontSize: 12.5, fontStyle: 'italic', wordBreak: 'break-word' }}>
                          &ldquo;{selectedStar.moolaMantra}&rdquo;
                        </p>
                      </div>
                    </div>

                    {/* Star Details CTA */}
                    <Link
                      to={`/nakshatra-homam/nakshatra/${selectedStar.slug}`}
                      className="btn-star-card-details"
                      style={{
                        width: '100%',
                        height: 48,
                        minHeight: 48,
                        maxHeight: 48,
                        padding: '0 20px',
                        borderRadius: 12,
                        background: 'linear-gradient(135deg, #f5c542 0%, #eab308 40%, #d4a017 100%)',
                        color: '#0a192f',
                        fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                        fontWeight: 800,
                        fontSize: 14.5,
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                        boxSizing: 'border-box',
                        textDecoration: 'none',
                        boxShadow: '0 4px 14px rgba(212, 160, 23, 0.35)',
                        marginTop: 24
                      }}
                    >
                      <span>View Full Day Details</span>
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= 3. BENEFITS OF NAKSHATRA HOMAM ================= */}
      <section className="homam-reg-benefits-sec">
        <div className="homam-container">
          <div className="homam-grid-head" style={{ textAlign: 'center', marginBottom: 36 }}>
            <div className="homam-emblem-badge" style={{ marginBottom: 10, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Sparkles className="w-3.5 h-3.5" style={{ color: '#b8860b' }} />
              <span>Sacred Divine Blessings</span>
            </div>
            <h2 style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)', fontWeight: 900, color: '#0f2d5c', margin: '0 0 6px 0' }}>
              11 Sacred Benefits of <span style={{ color: '#b45309' }}>Nakshatra Homam</span>
            </h2>
            <p className="tamil-sub" style={{ fontSize: 'clamp(1.05rem, 1.8vw, 1.35rem)', color: '#946608', fontWeight: 800, margin: '0 0 10px 0' }}>
              ஸ்ரீ நட்சத்திர ஹோமத்தில் பங்குபெறுவதால் குடும்பத்திற்கு கிட்டும் 11 திவ்ய பலன்கள்
            </p>
            <p style={{ fontSize: 14.5, color: '#475569', maxWidth: 780, margin: '0 auto' }}>
              Participating in the sacred 27 Nakshatra Homam channels profound cosmic and spiritual energy into your family lineage, dissolving karmic obstacles and awakening lasting auspiciousness.
            </p>
          </div>

          <div className="homam-benefits-grid">
            {HOMAM_BENEFITS.map((item) => (
              <div key={item.id} className="homam-benefit-card">
                <div className="homam-benefit-card-top">
                  <div className="homam-benefit-icon-wrapper">
                    {getBenefitIcon(item.iconName)}
                  </div>
                  <span className="homam-benefit-num">#{item.num}</span>
                </div>

                <h3 className="homam-benefit-title-ta">{item.titleTa}</h3>
                <span className="homam-benefit-title-en">{item.titleEn}</span>
                <p className="homam-benefit-desc">{item.descriptionTa}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= SUCCESS CONFIRMATION MODAL ================= */}
      {successModalData && (
        <div className="homam-reg-modal-overlay">
          <div className="homam-reg-modal-card">
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#dcfce7', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px auto' }}>
              <CheckCircle className="w-10 h-10" />
            </div>

            <h3 style={{ fontSize: '1.6rem', fontWeight: 800, color: '#0f2d5c', margin: '0 0 6px 0' }}>
              Registration Successful!
            </h3>
            <p style={{ color: '#64748b', fontSize: 14, margin: '0 0 16px 0' }}>
              Your sacred registration for the 27 Nakshatra Maha Homam has been recorded with Baba's grace.
            </p>

            <div className="homam-reg-token-badge">
              TOKEN: {successModalData.bookingId}
            </div>

            <div style={{ background: '#faf7f2', border: '1px solid #eaddc7', borderRadius: 12, padding: 16, textAlign: 'left', margin: '16px 0', fontSize: 13.5 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: '#64748b' }}>Devotee:</span>
                <strong style={{ color: '#0f2d5c' }}>{successModalData.name}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: '#64748b' }}>Birth Star:</span>
                <strong style={{ color: '#b45309' }}>{successModalData.star?.nameEn} ({successModalData.star?.nameTa})</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: '#64748b' }}>Homam Date:</span>
                <strong style={{ color: '#0f2d5c' }}>{successModalData.homamDate || 'Scheduled Star Day'}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#64748b' }}>Mobile:</span>
                <strong style={{ color: '#0f2d5c' }}>+91 {successModalData.mobile}</strong>
              </div>
            </div>

            <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.5 }}>
              Our Mandir Seva Team will contact you on your registered WhatsApp number to confirm your sankalpam details and homam arrangements.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 22 }}>
              <a
                href={templeWhatsApp}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  background: '#25D366',
                  color: '#ffffff',
                  padding: '12px 20px',
                  borderRadius: 12,
                  fontWeight: 700,
                  fontSize: 14,
                  textDecoration: 'none'
                }}
              >
                <WhatsAppIcon size={18} style={{ color: '#fff' }} />
                <span>Connect with Mandir Helpline</span>
              </a>

              <button
                type="button"
                onClick={() => setSuccessModalData(null)}
                style={{
                  background: '#0f2d5c',
                  color: '#ffffff',
                  padding: '12px 20px',
                  borderRadius: 12,
                  fontWeight: 700,
                  fontSize: 14,
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                Close & Register Another Devotee
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
